import { useEffect, useEffectEvent, useId } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { t } from 'i18next'
import { transparentize } from 'polished'

import { ArrowExitIcon } from '@pleio/icons/arrow-exit'
import { ArrowLeftIcon } from '@pleio/icons/arrow-left'
import { ArrowRightIcon } from '@pleio/icons/arrow-right'
import { TocCircleIcon } from '@pleio/icons/toc-circle'

import { AriaLiveMessage } from 'js/components/AriaLive'
import { DisplayDate } from 'js/components/DisplayDate'
import { Container } from 'js/components/Grid'
import HideVisually from 'js/components/HideVisually/HideVisually'
import IconButton from 'js/components/IconButton'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import Popover from 'js/components/Popover'
import HeaderLogo from 'js/magazine/components/HeaderLogo/HeaderLogo'
import ActionsButton from 'js/magazine-issue/components/ActionsButton'
import TableOfContents from 'js/magazine-issue/View/components/TableOfContents'

import { MagazineArticleViewFragment } from '../../../fragments/MagazineArticleView.fragment.generated'
import { useMagazineView } from '../../MagazineViewContext'

import styles from './Header.module.css'

type HeaderProps = {
    pathIsHome: boolean
    pathIsColophon: boolean
    currentArticle: MagazineArticleViewFragment | null
    themeColor: string
}

export const Header = ({
    pathIsHome,
    pathIsColophon,
    currentArticle,
    themeColor,
}: HeaderProps) => {
    const location = useLocation()
    const navigate = useNavigate()
    const exitKeyboardHintId = useId()
    const { magazineIssue, articles } = useMagazineView()

    const {
        url,
        issueNumber,
        container,
        timePublished,
        statusPublished,
        colophon,
    } = magazineIssue

    const currentArticleIndex = currentArticle
        ? articles.indexOf(currentArticle)
        : null

    const colophonWithValues = colophon.filter((field) => field.value)
    const colophonUrl = `${url}/colophon`

    const getAdjacent = (
        direction: 'prev' | 'next',
    ): { url: string; title: string } | null => {
        const isPrev = direction === 'prev'

        const index = isPrev
            ? pathIsColophon
                ? articles.length - 1
                : currentArticleIndex - 1
            : pathIsHome
              ? 0
              : Number.isFinite(currentArticleIndex)
                ? currentArticleIndex + 1
                : null

        const article = articles[index] ?? null

        if (article) {
            return {
                url: article.url,
                title: isPrev
                    ? t('entity-magazine.previous-article')
                    : t('entity-magazine.next-article'),
            }
        }

        if (isPrev) {
            return pathIsHome
                ? null
                : { url, title: t('entity-magazine.cover-page') }
        }
        if (pathIsColophon) {
            return null
        }
        return colophonWithValues.length
            ? { url: colophonUrl, title: t('entity-magazine.colophon') }
            : null
    }

    const prev = getAdjacent('prev')
    const next = getAdjacent('next')

    const buttonProps = {
        size: 'huge',
        variant: 'secondary',
        radiusStyle: 'rounded',
    } as const

    const shouldShowExitButton = !!location.state?.prevPathname
    const exitUrl = location.state?.prevPathname || container?.url || '/'
    const exitKeyboardHint = t('entity-magazine.exit-magazine-keyboard-hint')

    const handleEscape = useEffectEvent((event: KeyboardEvent) => {
        if (event.key !== 'Escape' || event.defaultPrevented) {
            return
        }

        event.preventDefault()
        navigate(exitUrl)
    })

    useEffect(() => {
        window.addEventListener('keydown', handleEscape, true)
        return () => window.removeEventListener('keydown', handleEscape, true)
    }, [])

    return (
        <header
            className={styles.wrapper}
            aria-describedby={exitKeyboardHintId}
            style={
                {
                    '--header-background-color': transparentize(
                        0.05,
                        themeColor,
                    ),
                } as React.CSSProperties
            }
        >
            <AriaLiveMessage message={exitKeyboardHint} />
            <HideVisually id={exitKeyboardHintId}>
                {exitKeyboardHint}
            </HideVisually>
            <Container size="huge" className="layout-header--container">
                <div className="layout-header--actions-left">
                    <Popover content={<TableOfContents />}>
                        <IconButton
                            {...buttonProps}
                            tooltip={t('entity-magazine.table-of-contents')}
                        >
                            <TocCircleIcon />
                        </IconButton>
                    </Popover>

                    <IconButton
                        {...buttonProps}
                        as={prev ? Link : undefined}
                        to={prev?.url}
                        state={{
                            prevPathname: location.state?.prevPathname,
                            direction: 'back',
                        }}
                        tooltip={prev?.title}
                        aria-label={`${t('action.previous')}: ${prev?.title}`}
                        disabled={!prev}
                        className="layout-header--navigation-button"
                    >
                        <ArrowLeftIcon />
                    </IconButton>
                    <IconButton
                        {...buttonProps}
                        as={next ? Link : undefined}
                        to={next?.url}
                        state={{
                            prevPathname: location.state?.prevPathname,
                            direction: 'forward',
                        }}
                        tooltip={next?.title}
                        aria-label={`${t('action.next')}: ${next?.title}`}
                        disabled={!next}
                        className="layout-header--navigation-button"
                    >
                        <ArrowRightIcon />
                    </IconButton>
                </div>

                <div className="layout-header--logo">
                    {!pathIsHome && (
                        <div className="layout-header--meta-wrapper">
                            <div className="layout-header--meta">
                                <Link to={url}>
                                    {container.title} {issueNumber}
                                </Link>
                                {statusPublished === 'published' && (
                                    <span>
                                        <DisplayDate
                                            date={timePublished}
                                            type="date"
                                            disableTooltip
                                        />
                                    </span>
                                )}
                            </div>

                            <ItemStatusTag
                                entity={magazineIssue}
                                style={{ marginLeft: 8 }}
                            />

                            <ActionsButton
                                entity={magazineIssue}
                                smallButton
                                style={{ marginLeft: 4 }}
                            />
                        </div>
                    )}
                    {pathIsHome && (
                        <>
                            <HeaderLogo
                                icon={container.icon}
                                title={container.title}
                                number={issueNumber}
                                subtitle={container.subtitle}
                                date={timePublished}
                            >
                                <ItemStatusTag
                                    entity={magazineIssue}
                                    style={{ marginLeft: 8 }}
                                />
                                <ActionsButton
                                    entity={magazineIssue}
                                    style={{ marginLeft: 4 }}
                                />
                            </HeaderLogo>
                        </>
                    )}
                </div>

                {shouldShowExitButton && (
                    <div className="layout-header--actions-right">
                        <IconButton
                            {...buttonProps}
                            tooltip={t('entity-magazine.exit-magazine')}
                            aria-description={exitKeyboardHint}
                            aria-keyshortcuts="Escape"
                            as={Link}
                            to={location.state?.prevPathname}
                        >
                            <ArrowExitIcon />
                        </IconButton>
                    </div>
                )}
            </Container>
        </header>
    )
}
